import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "./client";

export interface LeaderRow {
  persona: string;
  display_name: string;
  tagline: string | null;
  season: number;
  since: string;
  equity: number;
  starting_cash: number;
  return_pct: number;
  spy_return_pct: number;
  excess_pct: number;
  n_positions: number;
  cash: number;
  is_benchmark: boolean;
}

export interface NavPoint {
  day: string;
  equity: number;
  spy: number;
}

export interface HoldingOut {
  ticker: string;
  yahoo_symbol: string;
  name: string;
  horizon: string;
  shares: number;
  avg_cost: number;
  mark: number | null;
  market_value: number | null;
  unrealized: number | null;
  weight: number | null;
}

export interface TradeOut {
  ts: string;
  ticker: string;
  side: string;
  shares: number;
  price: number;
  gross: number;
  commission: number;
  reason: string;
}

export interface AttributionRow {
  horizon: string;
  market_value: number;
  unrealized: number;
}

export interface PortfolioDetail {
  persona: string;
  display_name: string;
  tagline: string | null;
  season: number;
  since: string;
  equity: number;
  cash: number;
  return_pct: number;
  spy_return_pct: number;
  excess_pct: number;
  nav: NavPoint[];
  holdings: HoldingOut[];
  trades: TradeOut[];
  attribution: AttributionRow[];
}

export function useLeaderboard() {
  return useQuery<LeaderRow[]>({
    queryKey: ["portfolio-leaderboard"],
    queryFn: async () => (await api.get<LeaderRow[]>("/portfolio")).data,
    staleTime: 60_000,
  });
}

export function usePortfolioDetail(persona: string | null) {
  return useQuery<PortfolioDetail>({
    queryKey: ["portfolio-detail", persona],
    queryFn: async () =>
      (await api.get<PortfolioDetail>(`/portfolio/${persona}`)).data,
    enabled: !!persona,
    staleTime: 60_000,
  });
}

export function useResetPortfolio() {
  const qc = useQueryClient();
  return useMutation<{ season: number; portfolios: number }, Error, void>({
    mutationFn: async () =>
      (await api.post<{ season: number; portfolios: number }>("/portfolio/reset")).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["portfolio-leaderboard"] });
      qc.invalidateQueries({ queryKey: ["portfolio-detail"] });
    },
  });
}
